import { useEffect, useState } from "react";
import { fetchAdminSiteMode, updateAdminSiteMode } from "../api";

const MODE_OPTIONS = [
  {
    id: "soon",
    showComingSoon: true,
    title: "Hamarosan",
    description: "A jelenlegi coming soon oldal látható a látogatóknak.",
  },
  {
    id: "live",
    showComingSoon: false,
    title: "Éles",
    description: "A teljes weboldal látható a látogatóknak.",
  },
];

function formatDate(value) {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat("hu-HU", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return null;
  }
}

function AdminSettings({ token, adminUsername }) {
  const [showComingSoon, setShowComingSoon] = useState(true);
  const [localOnly, setLocalOnly] = useState(false);
  const [productionShowComingSoon, setProductionShowComingSoon] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const data = await fetchAdminSiteMode(token);
        if (cancelled) return;
        setShowComingSoon(data.showComingSoon !== false);
        setLocalOnly(Boolean(data.localOnly));
        setProductionShowComingSoon(
          typeof data.productionShowComingSoon === "boolean"
            ? data.productionShowComingSoon
            : null,
        );
        setUpdatedAt(data.updatedAt || null);
        setUpdatedBy(data.updatedBy || "");
      } catch {
        if (!cancelled) setError("Nem sikerült betölteni a beállításokat.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSelect(nextShowComingSoon) {
    if (saving || nextShowComingSoon === showComingSoon) return;

    if (!nextShowComingSoon) {
      const confirmed = window.confirm(
        localOnly
          ? "Biztosan élesre váltasz helyben?\n\nCsak a localhost előnézet változik — a mizubrunch.hu éles oldal nem."
          : "Biztosan élesre váltasz?\n\nA teljes weboldal látható lesz minden látogató számára.",
      );
      if (!confirmed) return;
    }

    setSaving(true);
    setMessage("");
    setError("");

    try {
      const data = await updateAdminSiteMode(
        token,
        nextShowComingSoon,
        adminUsername || "",
      );
      setShowComingSoon(data.showComingSoon !== false);
      setLocalOnly(Boolean(data.localOnly));
      setUpdatedAt(data.updatedAt || null);
      setUpdatedBy(data.updatedBy || "");
      setMessage(
        data.localOnly
          ? data.showComingSoon
            ? "Helyi előnézet: Hamarosan oldal (az éles oldal nem változott)."
            : "Helyi előnézet: Éles oldal (az éles oldal nem változott)."
          : data.showComingSoon
            ? "Hamarosan oldal beállítva."
            : "Éles oldal beállítva.",
      );
    } catch {
      setError("A mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  }

  const formattedUpdatedAt = formatDate(updatedAt);

  return (
    <>
      <header className="admin-dash-header">
        <div>
          <h1 className="admin-dash-title">Beállítások</h1>
          <p className="admin-dash-subtitle">
            Weboldal mód váltása coming soon és éles között.
          </p>
        </div>
      </header>

      <section className="admin-settings-card">
        {loading ? (
          <p className="admin-dash-empty">Betöltés…</p>
        ) : (
          <>
            <p className="admin-settings-status">
              Jelenleg:{" "}
              <strong>{showComingSoon ? "Hamarosan oldal" : "Éles oldal"}</strong>
              {localOnly ? " (helyi előnézet)" : ""}
            </p>

            {localOnly && (
              <p className="admin-settings-note admin-settings-note-local">
                Fejlesztői mód: a váltás csak a localhoston érvényes. Az éles
                oldal jelenleg{" "}
                <strong>
                  {productionShowComingSoon === false
                    ? "éles"
                    : productionShowComingSoon === true
                      ? "hamarosan"
                      : "ismeretlen"}
                </strong>
                . A mizubrunch.hu csak éles környezetben változik.
              </p>
            )}

            <div className="admin-settings-modes">
              {MODE_OPTIONS.map((option) => {
                const isActive = showComingSoon === option.showComingSoon;
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`admin-settings-mode${isActive ? " is-active" : ""}`}
                    disabled={saving}
                    aria-pressed={isActive}
                    onClick={() => handleSelect(option.showComingSoon)}
                  >
                    <span className="admin-settings-mode-title">{option.title}</span>
                    <span className="admin-settings-mode-desc">{option.description}</span>
                  </button>
                );
              })}
            </div>

            {formattedUpdatedAt && (
              <p className="admin-settings-meta">
                Utoljára módosítva: {formattedUpdatedAt}
                {updatedBy ? ` (${updatedBy})` : ""}
              </p>
            )}

            <p className="admin-settings-note">
              A változás az oldal újratöltése után látszik a látogatóknak.
            </p>

            {message && (
              <p className="admin-settings-message" role="status">
                {message}
              </p>
            )}

            {error && (
              <p className="admin-email-error" role="alert">
                {error}
              </p>
            )}
          </>
        )}
      </section>
    </>
  );
}

export default AdminSettings;
